/**
 * VastUrlPlugin
 *
 * This plugin provides VAST URLs data for video ad slots.
 *
 * ## High-Level Overview:
 * Video ads require a VAST URL that includes all targeting parameters for proper ad delivery.
 * This plugin handles the creation of these URLs based on slot configuration, targeting parameters,
 * and player information. It provides a standardized way to obtain properly formatted VAST URLs
 * and/or the data for creating VAST URL that can be used by various video players.
 *
 * ## Responsibilities:
 * - Generate complete VAST URLs for video ad slots, including proper targeting parameters
 * - Provide ad unit path information for video slots
 * - Provide targeting parameters in the correct format and encoding for proper ad delivery.
 *
 * ## Public API:
 * This plugin exposes 3 public methods:
 * - get(slotName, player): Returns the complete VAST URL for the specified video slot
 * - getAdUnitPath(slotName): Returns the ad unit path for the specified video slot
 * - getTargetingParams(slotName, player): Returns the formatted targeting parameters as a string
 *
 */
import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import {
  getStaticParams,
  getPpidParam,
  setVideoAdUnitRuntimeVariable,
  getSlotSize,
} from "./VastUrlPluginUtils";
import type { VideoSlotName } from "../../config/platforms/types";
import type { AdNodeManager } from "../../core/AdNodeManager";
import {
  hasFeaturedVideoOnPage,
  isIncontentPlayerApplicable,
} from "../../helpers/hasVideoOnPage";
import type { FandomCmp } from "common-types";
import type { AdNode } from "../../core/AdNode";
import type { AdEngine } from "../../core/AdEngine";

const VAST_BASE_URL = "https://pubads.g.doubleclick.net/gampad/ads?";

interface VastUrlPluginApi {
  // TODO: https://fandom.atlassian.net/browse/COTECH-2346 Combine all methods into a single one
  get: (slotName: VideoSlotName, player?: string) => Promise<string>;
  getAdUnitPath: (slotName: VideoSlotName) => string;
  getTargetingParams: (
    slotName: VideoSlotName,
    player?: string,
  ) => Promise<string>;
  getPageTargeting: () => Record<string, string[] | string>;
  getSlotTargeting: (
    slotName: VideoSlotName,
  ) => Record<string, string[] | string>;
  setAdUnitRuntimeVariable: (slotName: VideoSlotName) => void;
  unblockVideo: () => void;
}

export class VastUrlPlugin extends AdEnginePlugin<VastUrlPluginApi> {
  private consentData: FandomCmp | undefined;
  private readonly resolvedState = Promise.withResolvers<void>();
  private adEngine?: AdEngine;

  constructor(
    private readonly config: {
      icFeaturedVideoPlayer?: string;
      icUAPJWPlayer?: boolean;
    } = {},
  ) {
    super();
  }

  /**
   * Create video ad slot based on the current page type.
   * This method is invoked in the `onAfterAdNodeManagerInit` hook to ensure
   * that the slot will be defined and all `api.get` methods have the
   * correct slot configuration available before being called.
   */
  private createAdNodeForPageType(adNodeManager: AdNodeManager): void {
    if (hasFeaturedVideoOnPage()) {
      adNodeManager.createAdNode("featured");
    }

    if (isIncontentPlayerApplicable()) {
      adNodeManager.createAdNode("incontent_player");
    }
  }

  private getParams(player: string | undefined, adNode: AdNode): string[] {
    const params = [
      ...getStaticParams(),
      ...getPpidParam(),
      ...this.getPrivacyParams(),
      ...this.getSlotParams(adNode),
      ...this.getCustomParams(player, adNode),
      ...this.getVideoParams(),
    ];

    return params.filter(Boolean);
  }

  private getSlotParams(adNode: AdNode): string[] {
    const adUnitPath = adNode.getAdUnitPath();
    const slotSize = getSlotSize(adNode.getSlotConfig());
    return [`iu=${adUnitPath}`, `sz=${slotSize}`];
  }

  private getPrivacyParams(): string[] {
    const { coppa, consentApi, consentString = "" } = this.consentData ?? {};
    const params: string[] = [];

    if (consentApi === "usp") {
      params.push(`us_privacy=${consentString}`, "gdpr=0");
    } else if (consentApi === "tcf") {
      params.push(`gdpr_consent=${consentString}`, "gdpr=1");
    }

    if (coppa === true) {
      params.push("tfcd=1");
    }

    return params;
  }

  private buildCustomParams(
    player: string | undefined,
    adNode: AdNode,
  ): string {
    const slotTargeting = adNode?.getAllTargeting() ?? {};

    const slotTargetingParams = Object.entries(slotTargeting)
      .map(([key, values]) => `${key}=${values.join(",")}`)
      .join("&");

    const pageTargetingParams = this.getPageTargetingParams();

    const params = [
      `player=${this.getVideoPlayer(player)}`,
      `rv=1`,
      slotTargetingParams,
      ...pageTargetingParams,
      ...this.getUAPParams(),
    ];

    return params.filter(Boolean).join("&");
  }

  private getCustomParams(
    player: string | undefined,
    adNode: AdNode,
  ): string[] {
    const params = this.buildCustomParams(player, adNode);
    return [`cust_params=${encodeURIComponent(params)}`];
  }

  private getPageTargetingParams(): string[] {
    const targeting =
      this.adEngine?.gptWrapper.getAllTargeting({ raw: false }) ?? {};
    return Object.entries(targeting).map(([key, value]) => `${key}=${value}`);
  }

  private getVideoPlayer(player?: string): string {
    return (
      player ??
      (this.config.icFeaturedVideoPlayer === "Connatix" ? "cnx" : "jwp")
    );
  }

  private getVideoParams(): string[] {
    return [`vpos=${this.getPosition()}`];
  }

  private getUAPParams() {
    const uap = this.adEngine?.gptWrapper?.getTargeting("uap");
    const uapC = this.adEngine?.gptWrapper?.getTargeting("uap_c");

    if (uap && uapC) {
      return [`uap=${uap[0]}`, `uap_c=${uapC[0]}`];
    }
    return [];
  }

  private getPosition(): string {
    return "preroll";
  }

  private waitForResolvedState(): Promise<void> {
    return this.resolvedState.promise;
  }

  api: VastUrlPluginApi = {
    /**
     * Provides the complete VAST URL for the specified slot name.
     * @param slotName - The name of the slot based on video slots config. Defaults to "featured".
     * @param player - The player type based on partner (e.g., "jwp" or "cnx").
     *                 If not provided, the player type is determined based on the ICBM.
     * @returns A promise that resolves to the complete VAST URL as a string. Returns an
     * empty string if the slot is not found.
     */
    get: async (slotName: VideoSlotName = "featured", player?: string) => {
      const adNode = this.adEngine?.adNodeManager.getAdNodeByName(slotName);
      if (!adNode) {
        return "";
      }

      await this.adEngine?.requestBids([adNode]);
      await this.waitForResolvedState();
      return `${VAST_BASE_URL}${this.getParams(player, adNode).join("&")}`;
    },

    /**
     * Provides the ad unit path for the specified video slot.
     * @param slotName - The name of the video slot from the configuration. Defaults to "featured".
     * @returns A promise that resolves to the ad unit path as a string.
     */
    getAdUnitPath: (slotName: VideoSlotName = "featured") => {
      const adNode = this.adEngine?.adNodeManager.getAdNodeByName(slotName);
      return adNode?.getAdUnitPath() ?? "";
    },

    /**
     * Provides the targeting parameters for the specified slot name.
     * @param slotName - The name of the slot based on the video slots configuration. Defaults to "featured".
     * @param player - The player type based on the partner (e.g., "jwp" or "cnx").
     *                 If not provided, the player type is determined based on the ICBM.
     * @returns A promise that resolves to the formatted targeting parameters as a string.
     */
    getTargetingParams: async (
      slotName: VideoSlotName = "featured",
      player?: string,
    ) => {
      const adNode = this.adEngine?.adNodeManager.getAdNodeByName(slotName);
      if (!adNode) {
        return "";
      }

      await this.adEngine?.requestBids([adNode]);
      await this.waitForResolvedState();
      return this.buildCustomParams(player, adNode);
    },

    /**
     * Retrieves all page-level targeting parameters.
     * These parameters are used to provide contextual information about the page
     * for video ad delivery.
     *
     * @returns An object where keys are targeting parameter names and values are
     * either strings or arrays of strings.
     */
    getPageTargeting: () => {
      return this.adEngine?.gptWrapper.getAllTargeting({ raw: false }) ?? {};
    },

    /**
     * Retrieves all slot-level targeting parameters for a specific video slot.
     * These parameters are used to provide contextual information about the slot
     * for ad delivery.
     *
     * @param slotName - The name of the video slot from the configuration.
     * @returns An object where keys are targeting parameter names and values are
     * either strings or arrays of strings.
     */
    getSlotTargeting: (slotName: VideoSlotName) => {
      return (
        this.adEngine?.gptWrapper.getAllSlotTargeting(slotName, {
          raw: false,
        }) ?? {}
      );
    },

    /**
     * Sets the ad unit path as a runtime variable for the specified video slot.
     * This method stores the ad unit path in the global window.ads.runtime.video object,
     * which is accessed by video player on the page to correctly configure ad requests.
     *
     * This is particularly important for the in-content player which needs to access
     * this information to build and load the VAST URL independently.
     *
     * @param slotName - The name of the video slot from the configuration.
     */
    setAdUnitRuntimeVariable: (
      slotName: VideoSlotName = "incontent_player",
    ) => {
      const adUnit = this.api.getAdUnitPath(slotName) ?? "";
      setVideoAdUnitRuntimeVariable(adUnit);
    },

    /**
     * Handles the unblocking of video playback.
     * This method behind the scenes resolves the {@link VastUrlPlugin.resolvedState} promise,
     * allowing video ad loading to continue.
     * This promise is used by the `get` and `getTargetingParams` methods that await
     * {@link VastUrlPlugin.waitForResolvedState} before returning the VAST URL or targeting parameters.
     *
     * For now, it is used by the TopStrategyTunerPlugin to unblock the video for Tentpole campaigns.
     *
     * @see VastUrlPlugin.resolvedState
     * @see VastUrlPlugin.waitForResolvedState
     * @see TopStrategyTunerPlugin.activateTentpoleFeature
     */
    unblockVideo: () => {
      this.resolvedState.resolve();
    },
  };

  hooks: Hooks = {
    onConsentUpdated: (consentData: FandomCmp) => {
      this.consentData = consentData;
    },

    onAfterAdNodeManagerInit: (adNodeManager: AdNodeManager) => {
      this.createAdNodeForPageType(adNodeManager);

      if (!this.config.icUAPJWPlayer) {
        this.api.unblockVideo();
      }
    },

    onAdEngineInit: (adEngine: AdEngine) => {
      this.adEngine = adEngine;
    },
  };
}
